/**
 * POS Dashboard - Main Point of Sale interface for club owners
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Package, ShoppingCart, CreditCard, BarChart3, 
  DollarSign, AlertTriangle, TrendingUp, Clock,
  ChevronRight, Plus, Settings, Users, ArrowLeft
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { posAPI } from '../../services/api';
import { toast } from 'sonner';

// Stat card component
const StatCard = ({ icon: Icon, label, value, subValue, color = "primary", onClick }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    whileHover={{ scale: onClick ? 1.02 : 1 }}
    onClick={onClick}
    className={`glass rounded-xl p-4 ${onClick ? 'cursor-pointer' : ''}`}
  >
    <div className="flex items-start justify-between mb-2">
      <div className={`w-10 h-10 rounded-lg bg-${color}/10 flex items-center justify-center`}>
        <Icon className={`w-5 h-5 text-${color}`} />
      </div>
    </div>
    <p className="text-2xl font-bold font-display">{value}</p>
    <p className="text-sm text-muted-foreground">{label}</p>
    {subValue && (
      <p className="text-xs text-muted-foreground mt-1">{subValue}</p>
    )}
  </motion.div>
);

// Quick action card
const QuickAction = ({ icon: Icon, label, description, onClick, variant = "default" }) => (
  <motion.button
    whileHover={{ scale: 1.02 }}
    whileTap={{ scale: 0.98 }}
    onClick={onClick}
    className={`w-full p-4 rounded-xl border text-left transition-colors ${
      variant === "primary" 
        ? "bg-primary/10 border-primary/30 hover:bg-primary/20" 
        : "bg-card border-border hover:bg-secondary"
    }`}
  >
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
        variant === "primary" ? "bg-primary/20" : "bg-secondary"
      }`}>
        <Icon className={`w-5 h-5 ${variant === "primary" ? "text-primary" : ""}`} />
      </div>
      <div className="flex-1">
        <p className="font-medium">{label}</p>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      <ChevronRight className="w-4 h-4 text-muted-foreground" />
    </div>
  </motion.button>
);

export default function POSDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [inventory, setInventory] = useState(null);
  const [todaySales, setTodaySales] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const today = new Date().toISOString().split('T')[0];
      
      const [sessionRes, inventoryRes, salesRes] = await Promise.all([
        posAPI.getCurrentSession(),
        posAPI.getInventoryReport(),
        posAPI.getSalesReport(today, today).catch(() => ({ data: { summary: {} } }))
      ]);

      setSession(sessionRes.data);
      setInventory(inventoryRes.data);
      setTodaySales(salesRes.data?.summary || {});
    } catch (err) {
      console.error('Error fetching POS data:', err);
      toast.error('Error cargando datos del POS');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="flex items-center justify-between p-4 max-w-6xl mx-auto">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/club')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-display font-bold text-lg">Punto de Venta</h1>
              <p className="text-xs text-muted-foreground">
                {session?.has_open_session ? 'Caja abierta' : 'Caja cerrada'}
              </p>
            </div>
          </div>
          
          {session?.has_open_session ? (
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              <span className="text-sm text-green-500">Activo</span>
            </div>
          ) : (
            <Button onClick={() => navigate('/pos/cash-session')} size="sm">
              Abrir Caja
            </Button>
          )}
        </div>
      </header>

      <div className="max-w-6xl mx-auto p-4 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            icon={DollarSign}
            label="Ventas hoy"
            value={formatCurrency(todaySales?.total_sales)}
            subValue={`${todaySales?.num_transactions || 0} transacciones`}
            color="green-500"
          />
          <StatCard
            icon={Package}
            label="Productos"
            value={inventory?.summary?.total_products || 0}
            subValue={`Valor: ${formatCurrency(inventory?.summary?.total_value)}`}
          />
          <StatCard
            icon={AlertTriangle}
            label="Stock bajo"
            value={inventory?.summary?.low_stock_count || 0}
            subValue={inventory?.summary?.low_stock_count > 0 ? 'Requiere atención' : 'Todo bien'}
            color={inventory?.summary?.low_stock_count > 0 ? "yellow-500" : "green-500"}
          />
          <StatCard
            icon={TrendingUp}
            label="Ganancia potencial"
            value={formatCurrency(inventory?.summary?.potential_profit)}
            subValue="En inventario"
          />
        </div>

        {/* Quick Actions */}
        <div>
          <h2 className="font-display font-bold text-lg mb-3">Acciones Rápidas</h2>
          <div className="grid gap-3 md:grid-cols-2">
            <QuickAction
              icon={ShoppingCart}
              label="Nueva Venta"
              description="Crear una venta en el punto de venta"
              onClick={() => navigate('/pos/sales')}
              variant="primary"
            />
            <QuickAction
              icon={Package}
              label="Gestionar Productos"
              description="Ver, crear y editar productos"
              onClick={() => navigate('/pos/products')}
            />
            <QuickAction
              icon={CreditCard}
              label="Caja"
              description="Abrir, cerrar y ver historial de caja"
              onClick={() => navigate('/pos/cash-session')}
            />
            <QuickAction
              icon={BarChart3}
              label="Reportes"
              description="Ver reportes de ventas e inventario"
              onClick={() => navigate('/pos/reports')}
            />
            <QuickAction
              icon={Users}
              label="Operadores"
              description="Gestionar usuarios del POS"
              onClick={() => navigate('/pos/users')}
            />
            <QuickAction
              icon={Settings}
              label="Configuración"
              description="Departamentos y categorías"
              onClick={() => navigate('/pos/settings')}
            />
          </div>
        </div>

        {/* Low Stock Alert */}
        {inventory?.low_stock?.length > 0 && (
          <div>
            <h2 className="font-display font-bold text-lg mb-3 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-500" />
              Productos con Stock Bajo
            </h2>
            <div className="glass rounded-xl divide-y divide-border">
              {inventory.low_stock.slice(0, 5).map((product) => (
                <div key={product.id} className="p-3 flex items-center justify-between">
                  <div>
                    <p className="font-medium">{product.name}</p>
                    <p className="text-xs text-muted-foreground">
                      SKU: {product.sku || 'N/A'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-yellow-500">{product.stock}</p>
                    <p className="text-xs text-muted-foreground">
                      Mín: {product.min_stock}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Cash Session Status */}
        {session?.session && (
          <div className="glass rounded-xl p-4">
            <h3 className="font-medium mb-3 flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Sesión de Caja Actual
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">Efectivo inicial</p>
                <p className="font-medium">{formatCurrency(session.session.initial_cash)}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Ventas efectivo</p>
                <p className="font-medium">{formatCurrency(session.session.summary?.cash_sales)}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Ventas tarjeta</p>
                <p className="font-medium">{formatCurrency(session.session.summary?.card_sales)}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Efectivo esperado</p>
                <p className="font-medium text-primary">{formatCurrency(session.session.summary?.expected_cash)}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
